/**
 * renderObjectLabelNode
 *
 * Pure DOM factory: turns the already-resolved label lines for a single cell
 * into an SVG <g> ready to be appended into the object-info-labels viewport
 * group. The caller computes anchor coordinates from the cell's bbox; this
 * function only knows about lines and where to put them.
 *
 * We render one <text> per line stacked vertically so users can copy-paste
 * a single field independently if they want, and so blank lines from skipped
 * fields don't collapse to a confusing empty space.
 */

const SVG_NS = 'http://www.w3.org/2000/svg'

/**
 * Sizes are in graph (paper) coordinate units, NOT screen pixels — the
 * overlay layer inherits paper's zoom/pan transform. With defaultObjectSize
 * = 100 and relationship overlay stroke-width = 0.75 as anchors, label
 * geometry is tuned to stay readable at typical work zoom (2x-5x) without
 * dominating the canvas.
 *
 * Iteration 2: further reduced after dense-map field test where adjacent
 * objects' labels overlapped horizontally. Combined with bbox-width-based
 * textLength clamping in renderObjectLabelNode, labels now never bleed past
 * their parent object's footprint.
 */

/** Spacing between consecutive text lines, in graph units. */
export const LABEL_LINE_HEIGHT = 5
/** Font size in graph units. Sized to feel like body-small at usual work zoom. */
export const LABEL_FONT_SIZE = 4
/** Vertical gap between an object's bottom edge and the first line of its label. */
export const LABEL_TOP_GAP = 1

export interface RenderObjectLabelArgs {
  /** Cell id, copied to data-cell-id so the hook can find/remove this node again. */
  cellId: string
  /** Lines to render, already filtered/ordered by extractLabelLines. */
  lines: string[]
  /** SVG x to translate the group to (object center, in graph coordinates). */
  anchorX: number
  /** SVG y to translate the group to (object bottom + gap). */
  anchorY: number
  /**
   * Maximum allowed text width in graph units (typically the object's bbox
   * width). When provided and a line's natural width would exceed this,
   * SVG `textLength` + `lengthAdjust=spacingAndGlyphs` compresses the line
   * horizontally so it stays within the parent object's footprint, which is
   * what stops adjacent objects' labels from visually colliding on dense
   * parking maps.
   */
  maxTextWidth?: number
  /**
   * Font size in graph units. Graphs have wildly different coordinate scales
   * (fresh CSV render vs. saved/normalized re-entry), so the caller computes
   * this from the median parking-spot size (parkingScaleMetrics). Defaults to
   * the legacy fixed LABEL_FONT_SIZE.
   */
  fontSize?: number
}

/**
 * Returns an `<g data-cell-id="...">` containing one `<text>` per line, or
 * null when there are no lines. The caller is responsible for appending the
 * returned element into the viewport overlay layer.
 */
export function renderObjectLabelNode(args: RenderObjectLabelArgs): SVGGElement | null {
  if (args.lines.length === 0) return null

  const fontSize = args.fontSize ?? LABEL_FONT_SIZE
  // 줄 간격은 폰트 크기에 비례 (기존 4px 폰트 ↔ 5px 줄간격 비율 유지).
  const lineHeight = fontSize * (LABEL_LINE_HEIGHT / LABEL_FONT_SIZE)

  const group = document.createElementNS(SVG_NS, 'g')
  group.setAttribute('data-cell-id', args.cellId)
  group.setAttribute('transform', `translate(${args.anchorX}, ${args.anchorY})`)
  // Belt-and-braces: the parent overlay group already sets pointer-events:none,
  // but setting it here too keeps this factory safe to drop into any container.
  group.setAttribute('pointer-events', 'none')

  args.lines.forEach((line, index) => {
    const text = document.createElementNS(SVG_NS, 'text')
    text.setAttribute('x', '0')
    text.setAttribute('y', String(index * lineHeight))
    text.setAttribute('text-anchor', 'middle')
    text.setAttribute('dominant-baseline', 'hanging')
    text.setAttribute('font-size', String(fontSize))
    text.setAttribute(
      'font-family',
      'system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
    )
    text.setAttribute('fill', 'currentColor')
    // textContent escapes — never use innerHTML here. Property names like NAME
    // can come from user input, so we treat them as untrusted text.
    text.textContent = line
    if (
      typeof args.maxTextWidth === 'number' &&
      args.maxTextWidth > 0 &&
      line.length > 0
    ) {
      // Heuristic: average glyph advance for our system-ui font at our font
      // size is roughly LABEL_FONT_SIZE * 0.6 graph units. If the line would
      // naturally exceed maxTextWidth, clamp horizontally via SVG textLength
      // so the renderer compresses the glyphs to fit. We don't apply
      // textLength to short lines because that would expand them into the
      // gap and reintroduce the very overlap we're trying to avoid.
      const estimatedNaturalWidth = line.length * fontSize * 0.6
      if (estimatedNaturalWidth > args.maxTextWidth) {
        text.setAttribute('textLength', String(args.maxTextWidth))
        text.setAttribute('lengthAdjust', 'spacingAndGlyphs')
      }
    }
    group.appendChild(text)
  })

  return group
}
